"use client"

import { callbackPages, ignoreRedirectRouteList } from "@/config/consts"
import {
  PushNavigateOptions,
  useConfigParams,
  useMounted,
  useNavigation,
} from "@/hooks"
import { useFlow } from "@/hooks/use-flow"
import { useAllCitiesQuery } from "@/services/get-all-cities"
import { useCountriesQuery } from "@/services/get-countries"
import useSignupRequestStore from "@/store/signup-request-store"
import { usePathname } from "next/navigation"
import { ReactNode, useEffect } from "react"

import { useAnalyticsContext } from "./analytics-provider"
import { Loader } from "./ui/loader"

/* -------------------------------------------------------------------------------------------------
 * types
 * -----------------------------------------------------------------------------------------------*/

type RegisterParamsComponentProps = {
  children: ReactNode
}

/* -------------------------------------------------------------------------------------------------
 * component
 * -----------------------------------------------------------------------------------------------*/

function RegisterParams({ children }: RegisterParamsComponentProps) {
  /*--------------------hooks--------------------*/
  const mounted = useMounted()
  const { setData } = useSignupRequestStore()
  const { fromPdv, mppartner, codicePromozione, intcmp } = useConfigParams()
  const pathName = usePathname()
  const router = useNavigation()
  const { initialFlowPath } = useFlow()

  const { isError: isAllCitiesError } = useAllCitiesQuery()
  const { isError: isAllCountriesError } = useCountriesQuery({
    current: false,
  })
  const { sendData: setAnalyticsData } = useAnalyticsContext()

  /*--------------------effects--------------------*/

  useEffect(() => {
    if (mppartner) {
      setData({ mppartner })
    }
  }, [mppartner, setData])

  useEffect(() => {
    if (isAllCountriesError || isAllCitiesError)
      return router.replace("/errore")

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAllCitiesError, isAllCountriesError])

  useEffect(() => {
    setData({ fromPdv })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromPdv])

  //save initial data into the store for later usage
  useEffect(() => {
    setData({
      initialHref: encodeURIComponent(window.location.href),
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  //save promocode and intcmp to the store cuz we need to clean them up from the search params :(
  useEffect(() => {
    if (codicePromozione || intcmp) {
      setAnalyticsData({ welcome_bonus_name: codicePromozione })
      setData({
        activePromoCode: codicePromozione,
        intcmpFromSearchParams: intcmp,
      })
    }
  }, [codicePromozione, intcmp, setAnalyticsData, setData])

  //handle initial redirect
  useEffect(() => {
    const routeOptions: PushNavigateOptions = {
      cleanupParams: ["intcmp", "codicePromozione", "codicepromozione"],
    }

    const canRedirectAutomatically = !ignoreRedirectRouteList.some(
      (value) => pathName === value,
    )

    //atterriamo da uno dei flussi " esterni "
    const isFromCallbackRedirectPage = callbackPages.some((callbackPage) =>
      pathName.includes(callbackPage),
    )

    if (!canRedirectAutomatically || isFromCallbackRedirectPage) return

    router.replace(initialFlowPath, routeOptions)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!mounted) return <Loader />

  return children
}

export { RegisterParams }
export type { RegisterParamsComponentProps }
